import { useMemo } from 'react';
import * as d3 from 'd3';
import { MindMapNode, ColorPalette } from '../types';
import { getFontSize, getPadding } from '../components/MindMap/utils';
import { getEmoji } from '../utils/emojiMap';
import { getContentColor } from '../utils/colorMap';

const MANDALA_RADIUS_GAP = 140;

export const useMindMapLayout = (
  data: MindMapNode | null,
  dimensions: { width: number; height: number },
  palette: ColorPalette,
  isMandalaMode: boolean,
  showEmoji: boolean,
  measureTextRef: React.MutableRefObject<HTMLCanvasElement | null>
) => {
// --- LAYOUT CALCULATION ---
  return useMemo(() => {
    if (!data || dimensions.width === 0) return { allNodes: [], links: [], floatingGroups: [], mandalaRings: [] };

    const getTextWidth = (text: string, fontSize: number, fontWeight: string = '500') => {
        const ctx = measureTextRef.current?.getContext('2d');
        if (!ctx) return text.length * fontSize * 0.7; 
        ctx.font = `${fontWeight} ${fontSize}px 'Inter', sans-serif`;
        const metrics = ctx.measureText(text);
        return metrics.width * 1.1; 
    };

    const splitText = (text: string, maxLength: number): string[] => {
        if (!text) return [""];
        if (text.length <= maxLength) return [text];
        const isJapanese = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf]/.test(text);
        if (!isJapanese) {
             const words = text.split(' ');
             const lines: string[] = [];
             let current = words[0];
             for (let i = 1; i < words.length; i++) {
                 if (current.length + 1 + words[i].length <= maxLength) current += " " + words[i];
                 else { lines.push(current); current = words[i]; }
             }
             if (current) lines.push(current);
             return lines;
        }
        const tokens = text.match(/[a-zA-Z0-9,\.\-_]+|\s+|[\s\S]/gu) || [];
        const lines: string[] = [];
        let currentLine = "";
        for (const token of tokens) {
            if (currentLine.length + token.length > maxLength && currentLine.length > 0) {
                lines.push(currentLine.trimEnd());
                currentLine = token.trimStart();
            } else {
                currentLine += token;
            }
        }
        if (currentLine) lines.push(currentLine.trimEnd());
        return lines;
    };
    
    const wrapTextToWidth = (text: string, maxWidth: number, fontSize: number): string[] => {
        const ctx = measureTextRef.current?.getContext('2d');
        if (!ctx) return text.split('\n');
        ctx.font = `400 ${fontSize}px 'Inter', sans-serif`;
        
        const resultLines: string[] = [];
        const rawLines = text.split('\n');
        
        for (const rawLine of rawLines) {
            const tokens = rawLine.match(/[a-zA-Z0-9,\.\-_]+|\s+|[\s\S]/gu) || [];
            let currentLine = "";
            
            for (const token of tokens) {
                if (!currentLine) {
                    currentLine = token.trimStart();
                    continue;
                }
                const testLine = currentLine + token;
                const width = ctx.measureText(testLine).width;
                if (width <= maxWidth) {
                    currentLine = testLine;
                } else {
                    resultLines.push(currentLine.trimEnd());
                    currentLine = token.trimStart();
                }
            }
            if (currentLine) resultLines.push(currentLine.trimEnd());
            else if (tokens.length === 0) resultLines.push("");
        }
        return resultLines;
    };

    const computeNodeDims = (d: any) => {
        if (d.data.isDummy) {
             d._dims = { width: 40, contentWidth: 40, height: 20 };
             d._spacing = { y: 22 };
             d._lines = [""];
             return;
        }
        const fs = getFontSize(d);
        const p = getPadding(d);
        const isFloatingRoot = d.data.isFloating || d._isFloatingTree;
        const fontWeight = d.depth === 0 && !isFloatingRoot ? '700' : '500';
        
        const rawName = d.data.name;
        let lines = rawName ? [rawName] : [""];
        if (!rawName) d._minWidth = 80; 
        
        const isJapanese = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf]/.test(rawName);
        const maxLen = isJapanese ? 10 : ((d.depth === 0 && !isFloatingRoot) ? 15 : 20);
        if (rawName.length > maxLen) lines = splitText(rawName, maxLen);

        d._lines = lines;
        let maxLineWidth = 0;
        lines.forEach(line => {
            const w = getTextWidth(line, fs, fontWeight);
            if (w > maxLineWidth) maxLineWidth = w;
        });

        if (d.data.description) {
            let descNaturalWidth = 0;
            const descFs = 11;
            const descLinesRaw = d.data.description.split('\n');
            descLinesRaw.forEach((line: string) => {
                const w = getTextWidth(line, descFs, '400');
                if (w > descNaturalWidth) descNaturalWidth = w;
            });
            const cappedDescWidth = Math.min(descNaturalWidth, 300);
            maxLineWidth = Math.max(maxLineWidth, cappedDescWidth);
        }

        const w = Math.max(maxLineWidth, d._minWidth || 0);
        const lineHeight = fs * 1.2;
        const titleTextHeight = lines.length * lineHeight;
        
        let h = Math.max(fs + p.y * 2, titleTextHeight + p.y * 2);
        
        const emoji = getEmoji(d.data.name);
        const showIcon = showEmoji && emoji && (d.depth > 0 || isFloatingRoot);
        const titleContentWidth = isFloatingRoot ? w + 24 + (showIcon ? 24 : 0) : w + p.x * 2 + (showIcon ? 24 : 0);

        let descHeight = 0;
        let descGap = 0;
        let descLines: string[] = [];
        if (d.data.description) {
             const descFs = 11;
             const maxDescWidth = titleContentWidth - 10; 
             descLines = wrapTextToWidth(d.data.description, maxDescWidth, descFs);
             descHeight = descLines.length * (descFs * 1.3) + 8;
             descGap = -6;
        }

        d._dims = { 
            width: titleContentWidth + 4, 
            contentWidth: titleContentWidth, 
            textWidth: w, 
            height: h + descHeight + descGap, 
            titleHeight: h,
            descHeight: descHeight,
            descGap: descGap
        };
        d._descLines = descLines;
        
        d._spacing = { y: h + descHeight + descGap + 10 }; 
    };

    const processTree = (nodes: any[], side: 'left'|'right') => {
        if(!nodes.length) return [];
        const syntheticRoot = { ...data, children: nodes };
        const h = d3.hierarchy(syntheticRoot, d => d.id==='root' && ((side==='left' && data.isCollapsedLeft) || (side==='right' && data.isCollapsedRight)) ? null : (d.isCollapsed ? null : d.children));
        h.each(d => { if(d.depth===0) (d as any).depth=0; computeNodeDims(d); });
        const t = d3.tree().nodeSize([1,1]).separation((a,b) => ((a as any)._spacing.y+(b as any)._spacing.y)/2+20);
        const r = t(h as any);
        
        // Dynamic Level Calculation to prevent horizontal overlap
        const levelMaxW: {[key:number]: number} = {};
        r.each((d:any) => {
            if(d.depth > 0) {
                const w = d._dims.width;
                if(!levelMaxW[d.depth] || levelMaxW[d.depth] < w) levelMaxW[d.depth] = w;
            }
        });
        
        const rootW = (r as any).data._dims ? (r as any).data._dims.width : 120;
        const levelStartX: {[key:number]: number} = {};
        
        let currentX = rootW/2 + 55; 
        
        let maxD = 0;
        r.each((d:any) => maxD = Math.max(maxD, d.depth));
        
        for(let d=1; d<=maxD; d++) {
             const w = levelMaxW[d] || 100;
             levelStartX[d] = currentX;
             currentX += w + 28;
        }

        const rootY = r.x;
        r.each((d:any) => {
            let sx = 0, sy = 0;
            if(d.depth > 0) { 
                const startX = levelStartX[d.depth];
                const nodeW = d._dims.width;
                sx = side === 'left' ? -(startX + nodeW/2) : startX + nodeW/2; 
                sy = d.x - rootY; 
            }
            d._cartesian = { baseX: sx, baseY: sy, x: sx+(d.data.offset?.x||0), y: sy+(d.data.offset?.y||0) };
            d._side = side;
        });
        return r.descendants().filter(d => d.depth>0);
    };
    
    const processBand = (nodes: any[], side: 'top'|'bottom') => {
        if(!nodes.length) return [];
        let isRootCollapsed = side === 'top' ? data.isCollapsedTop : data.isCollapsedBottom;
        if(isRootCollapsed) return [];
        const processedNodes: any[] = [];
        let currentY = side === 'top' ? -100 : 100;
        
        nodes.forEach(mainNodeData => {
            const h = d3.hierarchy(mainNodeData, d => d.isCollapsed ? null : d.children);
            h.each(d => { (d as any).depth+=1; computeNodeDims(d); });
            const t = d3.tree().nodeSize([1,1]).separation((a,b) => ((a as any)._spacing.y+(b as any)._spacing.y)/2+20);
            const subRoot = t(h as any);
            
            // Dynamic Levels for Band
            const levelMaxW: {[key:number]: number} = {};
            subRoot.each((d:any) => {
                 const realDepth = d.depth + 1;
                 const w = d._dims.width;
                 if(!levelMaxW[realDepth] || levelMaxW[realDepth] < w) levelMaxW[realDepth] = w;
            });
            
            const levelStartX: {[key:number]: number} = {};
            const w1 = levelMaxW[1] || 100;
            levelStartX[1] = -w1/2; // Center is 0, so left edge is -w1/2
            
            let currentX = w1/2 + 28;
            let maxD = 0; subRoot.each((d:any) => maxD = Math.max(maxD, d.depth+1));
            
            for(let d=2; d<=maxD; d++) {
                 const w = levelMaxW[d] || 100;
                 levelStartX[d] = currentX;
                 currentX += w + 28;
            }

            let minY = Infinity, maxY = -Infinity;
            subRoot.each(d => { if(d.x < minY) minY=d.x; if(d.x > maxY) maxY=d.x; });
            const yOffset = currentY - subRoot.x;
            
            subRoot.each((d:any) => {
                const realDepth = d.depth + 1;
                const nodeW = d._dims.width;
                let sx = 0;
                if (realDepth === 1) {
                    sx = 0;
                } else {
                    const startX = levelStartX[realDepth];
                    sx = startX + nodeW/2;
                }
                const sy = d.x + yOffset;
                d._cartesian = { baseX: sx, baseY: sy, x: sx+(d.data.offset?.x||0), y: sy+(d.data.offset?.y||0) };
                d._side = side;
            });
            processedNodes.push(...subRoot.descendants());
            let placedMaxY = -Infinity, placedMinY = Infinity;
            subRoot.each((d:any) => {
                const h = d._spacing.y;
                if(d._cartesian.baseY + h/2 > placedMaxY) placedMaxY = d._cartesian.baseY + h/2;
                if(d._cartesian.baseY - h/2 < placedMinY) placedMinY = d._cartesian.baseY - h/2;
            });
            const gap = 15;
            if(side === 'top') currentY = placedMinY - gap; else currentY = placedMaxY + gap;
        });
        return processedNodes;
    };

    const processMandala = (orderedChildren: any[]) => {
        const syntheticRoot = { ...data, children: orderedChildren };
        const h = d3.hierarchy(syntheticRoot, d => d.isCollapsed ? null : (d.children ? d.children.filter((c:any) => !c.isFloating) : null));
        let maxD = 0; h.each(d => maxD = Math.max(maxD, d.depth));
        const fill = (n:any) => {
            if(n.depth<maxD) {
                if(!n.children) {
                    const dummy = d3.hierarchy({id:`dummy-${n.data.id}-${n.depth}`, isDummy:true, name: "", children:[]});
                    (dummy as any).parent=n; (dummy as any).depth=n.depth+1; n.children=[dummy]; fill(dummy);
                } else n.children.forEach(fill);
            }
        };
        fill(h);
        h.each(d => computeNodeDims(d));
        let dynamicGap = 140;
        const levelWidths: { [key: number]: number } = {};
        h.each((d:any) => {
            if (d.depth > 0) levelWidths[d.depth] = (levelWidths[d.depth] || 0) + (d._dims?.width || 100) + 40;
        });
        Object.keys(levelWidths).forEach(k => {
            const d = parseInt(k);
            const reqGap = levelWidths[d] / (2 * Math.PI * d);
            if (reqGap > dynamicGap) dynamicGap = reqGap;
        });

        const t = d3.tree().size([2*Math.PI, 1]).separation((a,b) => ((a as any)._dims.width + (b as any)._dims.width)/2 * (a.parent===b.parent ? 1.1:2.2) / (a.depth||1));
        const r = t(h as any);
        const nodes: any[] = [];
        r.each((d:any) => {
            const angle = d.x - Math.PI/2;
            const rad = d.depth * dynamicGap;
            const sx = Math.cos(angle)*rad, sy = Math.sin(angle)*rad*0.65;
            d._cartesian = { baseX: sx, baseY: sy, x: sx+(d.data.offset?.x||0), y: sy+(d.data.offset?.y||0) };
            d._side = 'mandala'; d.angle = angle;
            nodes.push(d);
        });
        return { nodes, dynamicGap };
    };

    const rootChildren = data.children || [];
    const floatingNodes: MindMapNode[] = [];
    const leftNodes: MindMapNode[] = [];
    const rightNodes: MindMapNode[] = [];
    const topNodes: MindMapNode[] = [];
    const bottomNodes: MindMapNode[] = [];
    const SIDE_CAPACITY = 10;

    rootChildren.forEach((child, index) => {
        if (child.isFloating) floatingNodes.push(child);
        else {
            if (child.quadrant) {
                if (child.quadrant === 'left') leftNodes.push(child);
                else if (child.quadrant === 'top') topNodes.push(child);
                else if (child.quadrant === 'bottom') bottomNodes.push(child);
                else rightNodes.push(child);
            } else {
                if (index < SIDE_CAPACITY) {
                    if (index % 2 === 0) rightNodes.push(child); else leftNodes.push(child);
                } else {
                    const overflow = index - SIDE_CAPACITY;
                    const mod = overflow % 4;
                    if (mod === 0) topNodes.push(child);
                    else if (mod === 1) bottomNodes.push(child);
                    else if (mod === 2) rightNodes.push(child);
                    else leftNodes.push(child);
                }
            }
        }
    });
    
    let allNodes: any[] = [];
    
    let floatingGroups: any[] = [];
    floatingNodes.forEach(f => {
        const h = d3.hierarchy(f, d => d.isCollapsed?null:d.children);
        h.each(d => { (d as any)._isFloatingTree=true; computeNodeDims(d); });
        const t = d3.tree().nodeSize([1,1]).separation((a,b) => ((a as any)._spacing.y+(b as any)._spacing.y)/2+6);
        const r = t(h as any);
        let minX=Infinity, maxX=-Infinity, minY=Infinity, maxY=-Infinity;
        r.each((d:any) => {
            const sx = d.depth * 140, sy = d.x;
            const offX = (r.data as any).offset?.x||0, offY = (r.data as any).offset?.y||0;
            
            // Fix double offset logic for floating roots
            let baseX, baseY;
            if (d.depth === 0) {
                 baseX = 0;
                 baseY = 0;
            } else {
                 baseX = sx + offX;
                 baseY = sy + offY;
            }

            d._cartesian = { baseX, baseY, x: baseX+(d.data.offset?.x||0), y: baseY+(d.data.offset?.y||0) };
            if(d._cartesian.x < minX) minX=d._cartesian.x; if(d._cartesian.x > maxX) maxX=d._cartesian.x;
            if(d._cartesian.y < minY) minY=d._cartesian.y; if(d._cartesian.y > maxY) maxY=d._cartesian.y;
        });
        allNodes.push(...r.descendants());
        if(r.descendants().length>=1) floatingGroups.push({ cx:(minX+maxX)/2, cy:(minY+maxY)/2, width:maxX-minX, height:maxY-minY });
    });

    const rootH = d3.hierarchy(data); computeNodeDims(rootH);
    (rootH as any)._cartesian = {baseX:0, baseY:0, x:data.offset?.x||0, y:data.offset?.y||0}; (rootH as any)._side='root';
    
    let mandalaRings: any[] = [];
    let mandalaRadiusGap = 140;

    if(isMandalaMode) {
        const orderedChildren = [
            ...rightNodes,
            ...bottomNodes,
            ...[...leftNodes].reverse(),
            ...[...topNodes].reverse()
        ];
        const { nodes: mNodes, dynamicGap } = processMandala(orderedChildren);
        mandalaRadiusGap = dynamicGap;
        allNodes = [...mNodes, ...allNodes];
        const depths: any = {};
        mNodes.forEach(d => { if(d.depth>0) { if(!depths[d.depth]) depths[d.depth]=[]; depths[d.depth].push(d); }});
        Object.keys(depths).forEach(k => {
            const d = parseInt(k);
            depths[d].sort((a:any,b:any) => a.x - b.x);
            mandalaRings.push({depth: d, nodes: depths[d]});
        });
    } else {
        allNodes = [rootH as any, ...processTree(rightNodes,'right'), ...processTree(leftNodes,'left'), ...processBand(topNodes, 'top'), ...processBand(bottomNodes, 'bottom'), ...allNodes];
    }

    const assignColor = (n:any, pColor:string|null) => {
        if(n.depth===0 && !n._isFloatingTree && !n.data.isFloating) n._color = palette.colors[0];
        else if((n.depth===1 && !n._isFloatingTree) || (n.depth===0 && (n._isFloatingTree || n.data.isFloating))) {
            let h = 0; for(let i=0;i<n.data.id.length;i++) h = n.data.id.charCodeAt(i) + ((h<<5)-h);
            n._color = palette.colors[Math.abs(h)%palette.colors.length];
            if(palette.name==="Dynamic") { const c=getContentColor(n.data.name); if(c) n._color=c; }
        } else {
            if(n.data.isDummy) n._color = palette.type==='dark'?'#000':'#cbd5e1';
            else if(pColor) {
                const c = d3.hsl(pColor);
                if(palette.type==='light') { c.l = Math.min(0.9, 0.82+(n.depth%3)*0.03); c.s = Math.max(0.4, c.s); }
                else { c.l = Math.min(0.9, c.l+0.05); c.s = Math.max(0.3, c.s-0.05); }
                n._color = c.formatHex();
            } else n._color = palette.text;
            if(palette.name==="Dynamic" && !n.data.isDummy) { const c=getContentColor(n.data.name); if(c) n._color=c; }
        }
        if(n.children) n.children.forEach((c:any) => assignColor(c, n._color));
    };
    allNodes.filter(n => n.depth===0 || n.depth===1).forEach(n => assignColor(n, null));

    const links: any[] = [];
    allNodes.forEach(n => {
        if (isMandalaMode && n._side === 'mandala') return;
        if(n.parent && !n.data.isFloating && !n.parent.data.isFloating && !n.data.isDummy && !n.parent.data.isDummy) links.push({source:n.parent, target:n});
    });

    return { allNodes, links, floatingGroups, mandalaRings, mandalaRadiusGap };
  }, [data, dimensions, palette, isMandalaMode, showEmoji]);
};
